// Shared constants and pure logic for the GASG campaign landing page.
// Used by both the page components and the /api/gasg-booking route.

export interface GasgPool {
    slug: string; // form value + /api/slots pool key
    label: string; // shown in the UI
    dbName: string; // exact pools.pool_name in Supabase
}

export const GASG_POOLS: GasgPool[] = [
    { slug: "bishan", label: "Bishan", dbName: "Bishan" },
    { slug: "hougang", label: "Hougang", dbName: "Hougang" },
    { slug: "canberra", label: "Bukit Canberra", dbName: "Bukit Canberra" },
    { slug: "pasir-ris", label: "Pasir Ris", dbName: "Pasir Ris" },
    { slug: "jurong-west", label: "Jurong West", dbName: "Jurong West" },
    { slug: "clementi", label: "Clementi", dbName: "Clementi" }
];

export const CAMPAIGN_START = "2026-07-29";
export const CAMPAIGN_END = "2026-08-09";

// Static trial slots from the original landing page: weekend-only,
// same two times every Saturday and Sunday.
export const GASG_TRIAL_TIMES = ["9:45 am", "4:30 pm"];

// How many months ahead the booking calendar can be browsed
export const GASG_BROWSE_MONTHS = 5;

/**
 * Build the static weekend slot map used by the GASG form:
 * every Saturday/Sunday from `start` through the end of the month
 * `GASG_BROWSE_MONTHS` months ahead, each with GASG_TRIAL_TIMES.
 */
export function buildGasgSlotsByDate(
    start: Date = new Date()
): Record<string, string[]> {
    const from = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
    );
    // Last day of the month GASG_BROWSE_MONTHS ahead
    const until = new Date(
        from.getFullYear(),
        from.getMonth() + GASG_BROWSE_MONTHS + 1,
        0
    );
    const slotsByDate: Record<string, string[]> = {};
    for (const d = new Date(from); d <= until; d.setDate(d.getDate() + 1)) {
        const wd = d.getDay();
        if (wd === 0 || wd === 6) {
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            slotsByDate[iso] = GASG_TRIAL_TIMES;
        }
    }
    return slotsByDate;
}

export function isCampaignDay(iso: string): boolean {
    return iso >= CAMPAIGN_START && iso <= CAMPAIGN_END;
}

export function normalizePhone(raw: string): string {
    return "65" + raw.replace(/\D/g, "");
}

/**
 * Build a Monday-first month grid: leading nulls for offset days,
 * then ISO YYYY-MM-DD strings for each day of the month.
 * `month` is 0-indexed (JS Date convention).
 */
export function buildMonthGrid(year: number, month: number): (string | null)[] {
    const offset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const grid: (string | null)[] = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
        grid.push(
            `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`
        );
    }
    return grid;
}

export interface GasgFormInput {
    pool: string;
    date: string;
    timeslot: string;
    childName: string;
    childAge: string;
    parentName: string;
    phone: string;
    email: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateGasgForm(input: GasgFormInput): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!input.pool) errors.pool = "Please choose a pool.";
    if (!input.date) errors.date = "Please pick a date.";
    if (!input.timeslot) errors.timeslot = "Please pick a time.";
    if (!input.childName.trim())
        errors.childName = "Please enter your child's name.";
    const age = parseInt(input.childAge, 10);
    if (isNaN(age) || age < 4 || age > 16)
        errors.childAge = "Please enter an age between 4 and 16.";
    if (!input.parentName.trim()) errors.parentName = "Please enter your name.";
    if (!/^\d{8}$/.test(input.phone.replace(/\s+/g, "")))
        errors.phone = "Please enter a valid 8-digit Singapore number.";
    if (!EMAIL_RE.test(input.email.trim()))
        errors.email = "Please enter a valid email address.";
    return errors;
}
